import { Stack, Text } from '@mantine/core'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import { ELECTIONS_ADDRESS } from '@/constants/blockchain'
import OMMAElection from '@/contracts/OMMAElection'
import { useChainFunctions } from '@/hooks/useChain'
import useProfilePath from '@/hooks/useProfilePath'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

interface ActionNominationProps {
  close: () => void
  action: 'accept' | 'reject'
  candidate: string
  beforeAccept?: () => void
}

const ActionNomination: React.FC<ActionNominationProps> = ({
  close,
  action,
  candidate,
  beforeAccept,
}) => {
  const { t } = useTranslation()
  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)
  const navigate = useNavigate()

  // ready confirmation
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)
  const profilePath = useProfilePath(candidate)

  const handleAccept = useCallback(async () => {
    try {
      setIsLoading(true)
      if (beforeAccept) {
        beforeAccept()
      }
      await writeContract({
        address: ELECTIONS_ADDRESS,
        abi: OMMAElection,
        functionName: action === 'accept' ? 'acceptNomination' : 'rejectNomination',
        args: [candidate],
      })
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message)
    }
  }, [candidate, writeContract, action, beforeAccept])

  const handleClose = useCallback(() => {
    close()
  }, [close])

  useEffect(
    () => () => {
      setError('')
    },
    []
  )

  const handleConfirmationReady = useCallback(() => {
    navigate(profilePath)
    close()
  }, [navigate, close, profilePath])

  return (
    <>
      {error && (
        <ErrorMessage
          message={t(
            'components.governance.modal.actionNomination.errorMessage',
            'Error while {{action}} nomination',
            { action: action === 'accept' ? 'accepting' : 'rejecting' }
          )}
          details={error}
        />
      )}
      {isLoading && (
        <ConfirmationLoading
          title={t(
            'components.governance.modal.actionNomination.loadingMessage',
            '{{action}} Nomination',
            { action: action === 'accept' ? 'Accepting' : 'Rejecting' }
          )}
        />
      )}
      {isReady && !isLoading && !error && (
        <ConfirmationReady
          message={t(
            'components.governance.modal.actionNomination.readyMessage',
            'Nomination {{action}}',
            { action: action === 'accept' ? 'Accepted' : 'Rejected' }
          )}
          success={handleConfirmationReady}
          successMessage="Check Your Profile"
        />
      )}
      {!isReady && !error && !isLoading && (
        <>
          <Text>
            {t(
              'components.governance.modal.actionNomination.confirmMessage',
              'You are about to {{action}} your nomination to be a candidate in this election. This action is irreversible.',
              { action: action === 'accept' ? 'accept' : 'reject' }
            )}
          </Text>
          <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
            <RootButton onClick={handleClose} secondary style1={false} expand>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            <RootButton onClick={handleAccept} style1={false} expand>
              {t('buttons.confirm', 'Confirm')}
            </RootButton>
          </Stack>
        </>
      )}
    </>
  )
}

export default ActionNomination
